import { useMemo, useState } from 'react';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import { Box, Typography } from '@material-ui/core';
import { outcomeOf } from '../catalog/pipeline/outcome';
import { OutcomePill } from '../catalog/pipeline/OutcomePill';
import {
  isFailedDeployment,
  type DeploymentRecord,
} from '../deployments/snapshot';
import { useCardError, useHomeSources } from './homeSources';
import { formatDuration } from './formatDuration';
import { CustomerProjectChip } from '../CustomerProjectChip';
import { FilterSwitch } from './FilterSwitch';
import { searchesShown } from './searchesShown';

/**
 * Where the reader's choice of scope is remembered.
 *
 * Per viewer and per browser, which is what localStorage is for here: it is a
 * convenience, not state anything else reads back. Every access is wrapped
 * because it throws in a private window and can come back empty after a clear.
 */
const SCOPE_STORAGE_KEY = 'revenexx.recentDeployments.mineOnly';

function readStoredScope(): boolean {
  try {
    return window.localStorage.getItem(SCOPE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function storeScope(mineOnly: boolean): void {
  try {
    window.localStorage.setItem(SCOPE_STORAGE_KEY, String(mineOnly));
  } catch {
    // A viewer who cannot store a preference still gets a working switch.
  }
}

/** "a", "a or b", "a, b or c" — the filters that are on, as one phrase. */
function joinWith(word: 'and' | 'or', parts: Array<string | false>): string {
  const on = parts.filter((part): part is string => Boolean(part));
  if (on.length < 2) return on.join('');
  return `${on.slice(0, -1).join(', ')} ${word} ${on[on.length - 1]}`;
}

/** "3h ago" / "2d ago" — the card is read for recency, not for timestamps. */
function formatAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${Math.max(min, 1)}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Recent deployments across the watched orgs (DX-80).
 *
 * Reads the pre-aggregated snapshot from the github-insights backend
 * (`/deployments`), which computes it from the workflow runs the CI health poll
 * already pages — so this widget costs no GitHub calls of its own.
 *
 * The four absences are kept apart on purpose, because they are different facts
 * and a reader acts differently on each:
 *   - the collection has not run yet     → the card is still warming up
 *   - it ran and found no deployments    → nothing was deployed in the window
 *   - the request failed                 → the card says so
 *   - the reader narrowed it to nothing  → the deployments were not theirs
 * Collapsing any of them into an empty table would read as the card being
 * broken. The middle one is the state most weeks are in for most of the estate
 * — only 26 of 116 repositories deploy at all — and the last is the ordinary
 * result of the scope control while catalog ownership is still being populated.
 */
export const RecentDeployments = () => {
  // The page's one answer for the snapshot and for what the reader's team owns
  // (DX-307), which the Deployments figure above reads too — so the figure and this
  // card describe one snapshot, and the page asks for it once.
  const sources = useHomeSources();
  const { loading, data } = sources.deployments;
  const error = useCardError(sources.deployments.error, 'deployments');
  const [mineOnly, setMineOnly] = useState(readStoredScope);
  const [failedOnly, setFailedOnly] = useState(false);
  const [hideCustomerProjects, setHideCustomerProjects] = useState(false);
  /**
   * The repository slugs the reader's team owns, lower-cased for comparison.
   *
   * `undefined` until the catalog has answered — distinct from an empty set,
   * which is the real answer "your team owns nothing that deploys". Only the
   * second one should make a filtered card say it is empty. A catalog that did
   * not answer leaves it undefined too: the deployments are the card's subject
   * and come from elsewhere, so an unknown ownership set disables the filter
   * rather than emptying the list, which would look like "nothing deployed".
   * A token that carries no team owns nothing, which is the state every new
   * colleague is in until a GitHub team of the revenexx organisation has them.
   */
  const ownedSlugs = useMemo(
    () =>
      sources.owned.data
        ? new Set(
            sources.owned.data
              .map(c => c.slug)
              .filter((slug): slug is string => Boolean(slug)),
          )
        : undefined,
    [sources.owned.data],
  );

  /**
   * The rows the card shows, and how many the filters are holding back.
   *
   * A repository the catalog does not know counts as not owned. That is the
   * conservative reading and it is visible rather than silent: the hidden count
   * below says how many rows went, so a filter that hides everything reads as a
   * filter rather than as an empty week.
   */
  const mine = mineOnly ? ownedSlugs : undefined;
  const scopedToMine = mine !== undefined;
  const { visible, hidden } = useMemo(() => {
    const rows = data?.deployments ?? [];
    const kept = rows.filter(
      d =>
        (!mine || mine.has(d.slug.toLowerCase())) &&
        (!failedOnly || isFailedDeployment(d)) &&
        (!hideCustomerProjects || !d.customerProject),
    );
    return { visible: kept, hidden: rows.length - kept.length };
  }, [data, mine, failedOnly, hideCustomerProjects]);

  if (loading) {
    return (
      <InfoCard title="Recent deployments">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Recent deployments">
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  // The first collection cycle has not finished — distinct from "nothing was
  // deployed", which is a measurement.
  if (!data || data.generatedAt === null) {
    return (
      <InfoCard title="Recent deployments">
        <Typography variant="body2" color="textSecondary">
          Warming up — the first deployment snapshot is still being collected.
          Check back in a moment.
        </Typography>
      </InfoCard>
    );
  }

  const columns: TableColumn<DeploymentRecord>[] = [
    {
      title: 'Status',
      field: 'conclusion',
      width: '110px',
      // The portal's one outcome pill, so a deploy reads the same here, on the
      // Deployments page and on a component's CI/CD tab — and red exactly when
      // it counts in "· N failed" (isFailedDeployment reads the same verdict).
      render: row => <OutcomePill outcome={outcomeOf(row)} />,
      customFilterAndSearch: searchesShown<DeploymentRecord>(
        row => outcomeOf(row).label,
      ),
    },
    {
      title: 'Repository',
      field: 'slug',
      render: row => (
        <>
          <Link to={row.htmlUrl}>{row.slug}</Link>
          {row.customerProject && <CustomerProjectChip />}
        </>
      ),
    },
    {
      title: 'Workflow',
      field: 'workflow',
      render: row => (
        <Typography variant="body2" color="textSecondary">
          {row.workflow}
        </Typography>
      ),
    },
    {
      title: 'Branch',
      field: 'branch',
      width: '120px',
      render: row => row.branch ?? '—',
    },
    {
      title: 'Duration',
      field: 'durationSec',
      width: '100px',
      render: row => formatDuration(row.durationSec),
      customFilterAndSearch: searchesShown<DeploymentRecord>(row =>
        formatDuration(row.durationSec),
      ),
    },
    {
      title: 'When',
      field: 'createdAt',
      width: '110px',
      render: row => formatAge(row.createdAt),
      customFilterAndSearch: searchesShown<DeploymentRecord>(row =>
        formatAge(row.createdAt),
      ),
    },
  ];

  // The figures describe the rows on screen, which is why they are counted here
  // rather than read from `data.totals`: with the filter on, the snapshot's
  // totals describe the estate and the rows describe one team, and that is the
  // same contradiction AC-7 exists to prevent — a count above rows it does not
  // match. With the filter off the two are identical.
  const shownRepos = new Set(visible.map(d => d.slug)).size;
  const shownFailed = visible.filter(isFailedDeployment).length;
  const deploymentWord = visible.length === 1 ? 'deployment' : 'deployments';
  const repoWord = shownRepos === 1 ? 'repo' : 'repos';
  const failedNote = shownFailed > 0 ? ` · ${shownFailed} failed` : '';
  // Every filter that narrows the rows is named, so a card holding two rows
  // says why — and "My components" only once the catalog has said what is yours,
  // since until then it narrows nothing.
  const filterNotes = [
    scopedToMine && 'your components',
    failedOnly && 'failed only',
    hideCustomerProjects && 'no customer projects',
  ]
    .filter(Boolean)
    .map(note => ` · ${note}`)
    .join('');
  const subheader = `last ${data.windowDays}d · ${visible.length} ${deploymentWord} across ${shownRepos} ${repoWord}${failedNote}${filterNotes}`;

  // What the filters hold back, in the words of each: a deployment hidden by
  // one of them or by another, hence "or".
  const heldBack = joinWith('or', [
    scopedToMine && 'from components your team does not own',
    failedOnly && 'that did not fail',
    hideCustomerProjects && 'from customer projects',
  ]);
  // What the reader asked for, for the card the filters have emptied.
  const askedFor = joinWith('and', [
    scopedToMine && 'came from a component your team owns',
    failedOnly && 'failed',
    hideCustomerProjects && 'came from outside the customer projects',
  ]);
  // A switch for customer projects only where there is one to hide: a switch
  // that never changes the list reads as a filter that is broken.
  const hasCustomerProjects = data.deployments.some(d => d.customerProject);

  return (
    <InfoCard
      title="Recent deployments"
      subheader={subheader}
      deepLink={{ title: 'All deployments', link: '/deployments' }}
      action={
        <Box pr={2} display="flex" flexWrap="wrap" justifyContent="flex-end">
          <FilterSwitch
            label="My components"
            ariaLabel="Show only my components"
            checked={mineOnly}
            onChange={checked => {
              setMineOnly(checked);
              storeScope(checked);
            }}
          />
          <FilterSwitch
            label="Failed only"
            ariaLabel="Show only failed deployments"
            checked={failedOnly}
            onChange={setFailedOnly}
          />
          {hasCustomerProjects && (
            <FilterSwitch
              label="Hide customer projects"
              ariaLabel="Hide customer projects"
              checked={hideCustomerProjects}
              onChange={setHideCustomerProjects}
            />
          )}
        </Box>
      }
    >
      <Table<DeploymentRecord>
        columns={columns}
        data={visible}
        options={{
          // Counted over the window rather than over the rows on screen, so
          // the field does not vanish, its text still applied, when a switch
          // leaves five rows or fewer.
          search: data.deployments.length > 5,
          toolbar: data.deployments.length > 5,
          showTitle: false,
          searchFieldAlignment: 'left',
          paging: visible.length > 10,
          pageSize: 10,
          // A search leaving one row leaves one row, not nine blank ones.
          emptyRowsWhenPaging: false,
          padding: 'dense',
        }}
        emptyContent={
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">
              {hidden > 0 ? (
                <>
                  None of the last {data.windowDays} days' {hidden} deployments{' '}
                  {askedFor}.
                  {scopedToMine && (
                    <>
                      {' '}
                      Ownership is read from the catalog, so a repository with
                      no catalog entry — or one whose owner is not yet set —
                      counts as not yours.
                    </>
                  )}
                </>
              ) : (
                <>
                  No deployments in the last {data.windowDays} days. Most
                  repositories here do not deploy — libraries and SDKs publish
                  packages instead, which this card does not count.
                </>
              )}
            </Typography>
          </Box>
        }
      />
      {/* Said under the table rather than only in the empty state: a reader
          looking at four rows has no way to tell that thirty-eight are being
          held back, and a filter whose effect is invisible is one they stop
          trusting. */}
      {hidden > 0 && visible.length > 0 && (
        <Box px={2} pb={1}>
          <Typography variant="caption" color="textSecondary">
            {hidden} more {hidden === 1 ? 'deployment' : 'deployments'}{' '}
            {heldBack} {hidden === 1 ? 'is' : 'are'} hidden.
          </Typography>
        </Box>
      )}
    </InfoCard>
  );
};
